import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const SupportPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="text-sm text-gray-600">Support Center</p>
              </div>
            </Link>
            <Link
              to="/"
              className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">How Can We Help You?</h1>
          <p className="text-xl text-blue-100">
            Get the support you need to make the most of FBRSTAPP
          </p>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Live Chat */}
            <div className="bg-white rounded-xl p-6 shadow-lg border hover:shadow-xl transition-shadow">
              <div className="text-blue-600 mb-4">
                <ChatBubbleLeftRightIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Live Chat</h3>
              <p className="text-gray-600 mb-4">
                Get instant help from our support team. Available 24/7 for urgent issues.
              </p>
              <a 
                href="https://wa.me/923080323323?text=Hello%2C%20I%20need%20support%20with%20FBRSTAPP"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-block text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Start Chat
              </a>
            </div>

            {/* Phone Support */}
            <div className="bg-white rounded-xl p-6 shadow-lg border hover:shadow-xl transition-shadow">
              <div className="text-green-600 mb-4">
                <PhoneIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Phone Support</h3>
              <p className="text-gray-600 mb-4">
                Speak directly with our technical experts for complex issues.
              </p>
              <div className="space-y-2">
                <a 
                  href="https://wa.me/923080323323"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-gray-900 hover:text-green-600"
                >
                  +92-308-0323323
                </a>
                <p className="text-sm text-gray-600">Mon-Fri: 9 AM - 6 PM PKT</p>
              </div>
            </div>

            {/* Email Support */}
            <div className="bg-white rounded-xl p-6 shadow-lg border hover:shadow-xl transition-shadow">
              <div className="text-purple-600 mb-4">
                <EnvelopeIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 mb-4">
                Send us detailed questions and we'll respond within 24 hours.
              </p>
              <div className="space-y-2">
                <a 
                  href="mailto:askcom.pk@gmail.com"
                  className="font-semibold text-gray-900 hover:text-purple-600"
                >
                  askcom.pk@gmail.com
                </a>
                <p className="text-sm text-gray-600">Response within 24 hours</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Quick Support Links
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/help-center"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900">Help Center</h3>
                <p className="text-sm text-gray-600">Browse our knowledge base</p>
              </div>
            </Link>

            <Link 
              to="/training"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <QuestionMarkCircleIcon className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900">Training Resources</h3>
                <p className="text-sm text-gray-600">Learn how to use FBRSTAPP</p>
              </div>
            </Link>

            <Link 
              to="/community"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900">Community Forum</h3>
                <p className="text-sm text-gray-600">Connect with other users</p>
              </div>
            </Link>

            <Link 
              to="/contact-us"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <EnvelopeIcon className="h-6 w-6 text-orange-600 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900">Contact Us</h3>
                <p className="text-sm text-gray-600">Get in touch with our team</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Support Hours */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <ClockIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Support Hours</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Live Chat & Phone</h3>
                <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM PKT</p>
                <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM PKT</p>
                <p className="text-gray-600">Sunday: Closed</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Email Support</h3>
                <p className="text-gray-600">24/7 - We respond within 24 hours</p>
                <p className="text-gray-600">Priority support for paid plans</p>
                <p className="text-gray-600">Emergency issues: Within 4 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;